import { type RefObject, useEffect } from 'react';

type EventMap = {
    'poi-click': any[] | undefined;
    'navi-complete': any[] | undefined;
    'marker-click': any | undefined;
    'floor-changed': any | undefined;
    'zoom-changed'?: any;
    'void-click'?: any;
    'on-mouse-click'?: any;
    'on-mouse-move'?: any;
};

type EventCallbacks = {
    onPoiClick?: (data: any[]) => void;
    onNavigationComplete?: (data: any[]) => void;
    onMarkerClick?: (data: any) => void;
    onFloorChanged?: (data: any) => void;
    onZoomChanged?: (data: any) => void;
    onVoidClick?: (data: any) => void;
    onMouseClick?: (data: any) => void;
    onMouseMove?: (data: any) => void;
};

type Props<T extends HTMLElement> = {
    mapContainer?: RefObject<T | null>;
};

type HybridProps<T extends HTMLElement> = Props<T> & EventCallbacks;

export const useMapEvents = <T extends HTMLElement>({
    mapContainer,
    onPoiClick,
    onNavigationComplete,
    onMarkerClick,
    onFloorChanged,
    onZoomChanged,
    onVoidClick,
    onMouseClick,
    onMouseMove,
}: HybridProps<T>) => {
    useEffect(() => {
        if (!mapContainer?.current) return;

        const eventConfigs: {
            eventName: keyof EventMap;
            callback: ((val: any) => void) | undefined;
            transform: (detail: any) => any;
        }[] = [{ eventName: 'poi-click', callback: onPoiClick, transform: (d) => d }];

        if (onNavigationComplete) {
            eventConfigs.push({ eventName: 'navi-complete', callback: onNavigationComplete, transform: (d) => d });
        }

        if (onMarkerClick) {
            eventConfigs.push({ eventName: 'marker-click', callback: onMarkerClick, transform: (d) => d });
        }

        if (onFloorChanged) {
            eventConfigs.push({ eventName: 'floor-changed', callback: onFloorChanged, transform: (d) => d });
        }

        if (onZoomChanged) {
            eventConfigs.push({ eventName: 'zoom-changed', callback: onZoomChanged, transform: (d) => d });
        }

        if (onVoidClick) {
            eventConfigs.push({ eventName: 'void-click', callback: onVoidClick, transform: (d) => d });
        }

        if (onMouseClick) {
            eventConfigs.push({ eventName: 'on-mouse-click', callback: onMouseClick, transform: (d) => d });
        }

        if (onMouseMove) {
            eventConfigs.push({ eventName: 'on-mouse-move', callback: onMouseMove, transform: (d) => d });
        }

        const cleanups: (() => void)[] = [];
        const containerElement = mapContainer.current;

        eventConfigs.forEach(({ eventName, callback, transform }) => {
            if (callback && containerElement) {
                const handler = (e: CustomEvent) => callback(transform(e.detail));
                containerElement.addEventListener(eventName, handler as EventListener);
                cleanups.push(() => {
                    containerElement.removeEventListener(eventName, handler as EventListener);
                });
            }
        });

        return () => cleanups.forEach((fn) => fn());
    }, [mapContainer, onPoiClick, onNavigationComplete, onMarkerClick, onFloorChanged, onZoomChanged, onVoidClick, onMouseClick, onMouseMove]);
};
